import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

import { getUrl } from "../../helpers/url";

const initialState = {
  settingsActiveTab: "contact",
  contactsActiveTab: "syncRules",
  invoiceActiveTab: "syncRules",
  productActiveTab: "syncRules",
  isMsbcConnected: false,
  isEnvPreferencesSaved: false,
  isMsbcExtensionInstalled: false,
  environmentPreference: undefined,
  companyPreference: undefined,
  saveModalPreference: false,
  environment: {
    loading: false,
    environmentDetails: {},
    currentRequestId: undefined,
    error: "",
  },
  company: {
    loading: false,
    companyDetails: {},
    error: "",
    currentRequestId: undefined,
  },
  data: {
    loading: false,
    error: "",
    status: {},
  },
  selectedTabValues: {},
  checkExtensionInstalled: false,
  showExtensionModal: false,
  tenantId: undefined,
  errorMessage: "",
  successMessage: "",
  showSuccessToast: false,
  showErrorToast: false,
};

export const fetchStatus = createAsyncThunk(
  "preferences/fetchStatus",
  async ({ shop }, { dispatch }) => {
    try {
      const { data: response } = await axios.get(
        getUrl("REACT_APP_GET_STATUS"),
        {
          params: {
            id: shop,
          },
        }
      );
      return response;
    } catch (error) {
      console.log(error);
      let errorMessage;
      if (error.response && error.response.data.message)
        errorMessage = error.response.data.message;
      else errorMessage = error.message;
      dispatch(setShowErrorModal({ message: errorMessage }));
      throw error;
    }
  }
);

export const fetchEnvironment = createAsyncThunk(
  "preferences/fetchEnvironment",
  async ({ tenantId, shop }, { dispatch }) => {
    try {
      const { data: response } = await axios.get(
        `${getUrl(
          "REACT_APP_GET_ENVIRONMENTS"
        )}?tenantId=${tenantId}&shop=${shop}`
      );
      return response;
    } catch (error) {
      console.log(error);
      let errorMessage;
      if (error.response && error.response.data.message)
        errorMessage = error.response.data.message;
      else errorMessage = error.message;
      dispatch(setShowErrorModal({ message: errorMessage }));
      throw error;
    }
  }
);

export const fetchCompanies = createAsyncThunk(
  "preferences/fetchCompanies",
  async ({ tenantId, shop, environmentPreference }, { dispatch }) => {
    try {
      const { data: response } = await axios.get(
        `${getUrl(
          "REACT_APP_GET_COMPANIES"
        )}?tenantId=${tenantId}&shop=${shop}&environment=${environmentPreference}`
      );
      return response;
    } catch (error) {
      console.log(error);
      let errorMessage;
      if (error.response && error.response.data.message)
        errorMessage = error.response.data.message;
      else errorMessage = error.message;
      dispatch(setShowErrorModal({ message: errorMessage }));
      throw error;
    }
  }
);

export const preferenceSlice = createSlice({
  name: "preferences",
  initialState,
  reducers: {
    onChangeSettingsActiveTab(state, { payload }) {
      if (
        state.isMsbcConnected &&
        !payload.shopifyLoading &&
        !payload.msbcLoading &&
        state.settingsActiveTab === "contact" &&
        (payload.selectedTab === "invoice" ||
          payload.selectedTab === "product") &&
        (!payload.isContactsSyncRulesSaved ||
          !payload.isContactDefaultMappingsSaved ||
          !payload.isCustomerMappingsSaved)
      ) {
        state.selectedTabValues = {
          tab: payload.selectedTab,
          subTab: "syncRules",
        };
        state.saveModalPreference = true;
      } else if (
        state.isMsbcConnected &&
        !payload.shopifyLoading &&
        !payload.msbcLoading &&
        state.settingsActiveTab === "invoice" &&
        (payload.selectedTab === "contact" ||
          payload.selectedTab === "product") &&
        (!payload.isInvoiceSyncRulesSaved || !payload.isInvoiceMappingsSaved)
      ) {
        state.selectedTabValues = {
          tab: payload.selectedTab,
          subTab: "syncRules",
        };
        state.saveModalPreference = true;
      } else if (
        state.isMsbcConnected &&
        !payload.shopifyLoading &&
        !payload.msbcLoading &&
        state.settingsActiveTab === "product" &&
        (payload.selectedTab === "contact" ||
          payload.selectedTab === "invoice") &&
        (!payload.isProductMappingsSaved || !payload.isProductSyncRulesSaved)
      ) {
        state.selectedTabValues = {
          tab: payload.selectedTab,
          subTab: "syncRules",
        };
        state.saveModalPreference = true;
      } else {
        state.settingsActiveTab = payload.selectedTab;
      }
    },

    onChangeContactActiveTab(state, { payload }) {
      if (
        state.isMsbcConnected &&
        !payload.shopifyLoading &&
        !payload.msbcLoading &&
        state.contactsActiveTab === "syncRules" &&
        !payload.isContactsSyncRulesSaved &&
        (payload.selectedTab === "defaultMappings" ||
          payload.selectedTab === "fieldMappings")
      ) {
        state.selectedTabValues = {
          tab: "contact",
          subTab: payload.selectedTab,
        };
        state.saveModalPreference = true;
      } else if (
        state.isMsbcConnected &&
        !payload.shopifyLoading &&
        !payload.msbcLoading &&
        state.contactsActiveTab === "defaultMappings" &&
        !payload.isContactDefaultMappingsSaved &&
        (payload.selectedTab === "syncRules" ||
          payload.selectedTab === "fieldMappings")
      ) {
        state.selectedTabValues = {
          tab: "contact",
          subTab: payload.selectedTab,
        };
        state.saveModalPreference = true;
      } else if (
        state.isMsbcConnected &&
        !payload.shopifyLoading &&
        !payload.msbcLoading &&
        state.contactsActiveTab === "fieldMappings" &&
        !payload.isCustomerMappingsSaved &&
        (payload.selectedTab === "syncRules" ||
          payload.selectedTab === "defaultMappings")
      ) {
        state.selectedTabValues = {
          tab: "contact",
          subTab: payload.selectedTab,
        };
        state.saveModalPreference = true;
      } else {
        state.contactsActiveTab = payload.selectedTab;
      }
    },

    onChangeInvoiceActiveTab(state, { payload }) {
      if (
        state.isMsbcConnected &&
        !payload.shopifyLoading &&
        !payload.msbcLoading &&
        state.invoiceActiveTab === "syncRules" &&
        !payload.isInvoiceSyncRulesSaved &&
        payload.selectedTab === "fieldMappings"
      ) {
        state.selectedTabValues = {
          tab: "invoice",
          subTab: payload.selectedTab,
        };
        state.saveModalPreference = true;
      } else if (
        state.isMsbcConnected &&
        !payload.shopifyLoading &&
        !payload.msbcLoading &&
        state.invoiceActiveTab === "fieldMappings" &&
        !payload.isInvoiceMappingsSaved &&
        payload.selectedTab === "syncRules"
      ) {
        state.selectedTabValues = {
          tab: "invoice",
          subTab: payload.selectedTab,
        };
        state.saveModalPreference = true;
      } else {
        state.invoiceActiveTab = payload.selectedTab;
      }
    },

    onChangeProductActiveTab(state, { payload }) {
      if (
        state.isMsbcConnected &&
        !payload.shopifyLoading &&
        !payload.msbcLoading &&
        state.productActiveTab === "syncRules" &&
        !payload.isProductSyncRulesSaved &&
        payload.selectedTab === "fieldMappings"
      ) {
        state.selectedTabValues = {
          tab: "product",
          subTab: payload.selectedTab,
        };
        state.saveModalPreference = true;
      } else if (
        state.isMsbcConnected &&
        !payload.shopifyLoading &&
        !payload.msbcLoading &&
        state.productActiveTab === "fieldMappings" &&
        !payload.isProductMappingsSaved &&
        payload.selectedTab === "syncRules"
      ) {
        state.selectedTabValues = {
          tab: "product",
          subTab: payload.selectedTab,
        };
        state.saveModalPreference = true;
      } else {
        state.productActiveTab = payload.selectedTab;
      }
    },

    onChangeMsbcConnected(state, { payload }) {
      state.isMsbcConnected = payload.isConnected;
    },

    onChangeSaveModalPreference(state, { payload }) {
      state.saveModalPreference = payload.show;
    },

    onChangeCheckExtensionInstalled(state, { payload }) {
      state.checkExtensionInstalled = payload.connected;
    },

    setShowExtensionModal(state, { payload }) {
      state.showExtensionModal = payload.show;
    },

    onHideExtensionModal(state) {
      state.showExtensionModal = false;
    },

    setShowSuccessModal(state, { payload }) {
      state.successMessage = payload.message;
      state.showSuccessToast = true;
    },

    onHideSuccessModal(state) {
      state.successMessage = "";
      state.showSuccessToast = false;
    },

    setShowErrorModal(state, { payload }) {
      state.errorMessage = payload.message;
      state.showErrorToast = true;
    },

    onHideErrorModal(state) {
      state.errorMessage = "";
      state.showErrorToast = false;
    },

    setDropdownPreferences(state, { payload }) {
      if (payload.dropdownFor === "environment") {
        state.environmentPreference = payload.selectedOption;
      } else if (payload.dropdownFor === "company") {
        state.companyPreference = payload.selectedOption;
      }
      state.isEnvPreferencesSaved = false;
    },

    setIsEnvPreferencesSaved(state, { payload }) {
      state.isEnvPreferencesSaved = true;
    },

    onChangeTabValues(state) {
      window.scroll(0, 0);
      state.settingsActiveTab = state.selectedTabValues.tab;
      if (state.selectedTabValues.tab === "contact") {
        state.contactsActiveTab = state.selectedTabValues.subTab;
      } else if (state.selectedTabValues.tab === "product") {
        state.productActiveTab = state.selectedTabValues.subTab;
      } else {
        state.invoiceActiveTab = state.selectedTabValues.subTab;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchEnvironment.pending, (state, actions) => {
      if (!state.environment.loading) {
        state.environment.loading = true;
        state.environment.currentRequestId = actions.meta.requestId;
      }
    });
    builder.addCase(fetchEnvironment.fulfilled, (state, actions) => {
      if (
        state.environment.loading &&
        state.environment.currentRequestId === actions.meta.requestId
      ) {
        state.environment.environmentDetails = actions.payload;
        state.environment.loading = false;
        state.environment.error = "";
        state.environment.currentRequestId = undefined;
      }
    });
    builder.addCase(fetchEnvironment.rejected, (state, actions) => {
      if (
        state.environment.loading &&
        state.environment.currentRequestId === actions.meta.requestId
      ) {
        state.environment.loading = false;
        state.environment.environmentDetails = {};
        state.environment.error = actions.error.message ?? "";
        state.environment.currentRequestId = undefined;
      }
    });

    builder.addCase(fetchCompanies.pending, (state, actions) => {
      if (!state.company.loading) {
        state.company.loading = true;
        state.company.currentRequestId = actions.meta.requestId;
      }
    });
    builder.addCase(fetchCompanies.fulfilled, (state, actions) => {
      if (
        state.company.loading &&
        state.company.currentRequestId === actions.meta.requestId
      ) {
        state.company.loading = false;
        state.company.companyDetails = actions.payload;
        state.company.error = "";
        state.company.currentRequestId = undefined;
      }
    });
    builder.addCase(fetchCompanies.rejected, (state, actions) => {
      if (
        state.company.loading &&
        state.company.currentRequestId === actions.meta.requestId
      ) {
        state.company.loading = false;
        state.company.companyDetails = {};
        state.company.error = actions.error.message ?? "";
        state.company.currentRequestId = undefined;
      }
    });

    builder.addCase(fetchStatus.pending, (state, action) => {
      if (!state.data.loading) {
        state.data.loading = true;
      }
    });
    builder.addCase(fetchStatus.fulfilled, (state, action) => {
      if (state.data.loading) {
        state.data.loading = false;
        state.data.status = action.payload;
        state.data.error = "";

        const { creds, userData } = action.payload;
        if (creds?.msbc) state.isMsbcConnected = true;
        if (
          userData &&
          Object.keys(userData).length > 0 &&
          userData?.msbc &&
          Object.keys(userData).length > 0
        ) {
          state.tenantId = userData.msbc.tenantId;
        }
        if (userData && Object.keys(userData).length > 0) {
          state.environmentPreference = userData.msbc.environment ?? undefined;
          state.companyPreference = userData.msbc.companyId ?? undefined;
        }
      }
    });
    builder.addCase(fetchStatus.rejected, (state, action) => {
      if (state.data.loading) {
        state.data.loading = false;
        state.data.status = {};
        state.data.error = action.error.message ?? "";
      }
    });
  },
});

export const {
  onChangeSettingsActiveTab,
  onChangeContactActiveTab,
  onChangeInvoiceActiveTab,
  onChangeMsbcConnected,
  onChangeSaveModalPreference,
  onChangeTabValues,
  setDropdownPreferences,
  setIsEnvPreferencesSaved,
  onChangeCheckExtensionInstalled,
  setShowExtensionModal,
  onHideExtensionModal,
  onChangeProductActiveTab,
  setShowSuccessModal,
  onHideSuccessModal,
  setShowErrorModal,
  onHideErrorModal,
} = preferenceSlice.actions;

export default preferenceSlice.reducer;
